"use client";

import type { FC, ReactNode } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import type { IFilter } from "@/src/shared/features/search/types";
import { searchService } from "@/src/shared/features/search/services";

interface IMobileSearchModalProps {
  filters: IFilter;
  onClose: () => void;
  onSectionSelect: (section: "where" | "when" | "who") => void;
  handleSearch: () => void;
}

const RowIcon: FC<{ name: "where" | "dates" | "guests" }> = ({ name }) => (
  <img
    src={`/icons/search/modify_search_${name}.svg`}
    alt=""
    className="h-[22px] w-[22px] shrink-0"
  />
);

const SearchRow: FC<{
  icon: ReactNode;
  placeholder: string;
  value: string;
  onClick: () => void;
}> = ({ icon, placeholder, value, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-3 px-4 py-[18px] text-left"
  >
    {icon}
    <span
      className={`min-w-0 flex-1 truncate text-15 font-medium ${value ? "text-ink-black" : "text-ink-gray"}`}
    >
      {value || placeholder}
    </span>
  </button>
);

/**
 * The app's "Change your search" sheet (`ListingModifySearchSheet`): it drops from the top
 * over a dimmed page, with Where, dates and guests in one card and a Search button.
 */
const MobileSearchModal: FC<IMobileSearchModalProps> = ({
  filters,
  onClose,
  onSectionSelect,
  handleSearch,
}) => {
  const { checkIn, checkOut, guests, destination } = filters;
  const dates =
    checkIn && checkOut
      ? `${format(checkIn, "d MMM")} - ${format(checkOut, "d MMM")}`
      : "";
  const guestText = searchService.guestSummary(guests, destination?.name);

  const handleSearchClick = () => {
    handleSearch();
    onClose();
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        className="fixed inset-0 z-[44] bg-black/25 md:hidden"
      />
      <motion.div
        initial={{ y: "-100%" }}
        animate={{ y: 0 }}
        exit={{ y: "-100%" }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        role="dialog"
        aria-modal
        aria-label="Change your search"
        className="fixed inset-x-0 top-0 z-[45] rounded-b-[20px] bg-white px-4 pb-5 pt-[calc(16px+env(safe-area-inset-top))] md:hidden"
      >
        <div className="flex items-center">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-1"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6 text-ink-black"
              fill="currentColor"
              aria-hidden
            >
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
          <h2 className="flex-1 pr-8 text-center text-16 font-semibold text-ink-black">
            Change your search
          </h2>
        </div>

        <div className="mt-6 divide-y divide-[#E0E0E0]/60 rounded-2xl border border-[#E0E0E0]">
          <SearchRow
            icon={<RowIcon name="where" />}
            placeholder="Where"
            value={destination?.name ?? ""}
            onClick={() => onSectionSelect("where")}
          />
          <SearchRow
            icon={<RowIcon name="dates" />}
            placeholder="Check in - Check out"
            value={dates}
            onClick={() => onSectionSelect("when")}
          />
          <SearchRow
            icon={<RowIcon name="guests" />}
            placeholder="Guests"
            value={guestText}
            onClick={() => onSectionSelect("who")}
          />
        </div>

        <button
          type="button"
          onClick={handleSearchClick}
          className="mt-5 flex h-14 w-full items-center justify-center rounded-2xl bg-brand text-16 font-semibold text-white active:opacity-80"
        >
          Search
        </button>
      </motion.div>
    </>
  );
};

export default MobileSearchModal;
